/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { api, mutatingPut, type ApiResult } from "./client";

export type RegistryAddress = {
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
};

export type ClientStats = {
  timeframe: string;
  referredClientCount: number;
  activeClientCount: number;
  totalClientCount: number;
};

export type ReferralActivity = {
  lastReferralAt: string | null;
  allTimeCount: number;
  monthlyCount: number;
};

export type OrganizationSummary = {
  id: number;
  name: string;
  stateCode: string | null;
  partnerType: string | null;
  canReceiveUpdates: boolean;
  providerCount: number;
  address: RegistryAddress | null;
  createdAt: string | null;
};

export type ProviderSummary = {
  id: number;
  organizationId: number | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phoneNumber: string | null;
  faxNumber: string | null;
  receiveUpdates: boolean;
  referralActivity: ReferralActivity;
  clientStats: ClientStats[];
};

export type OrganizationDetail = {
  organization: OrganizationSummary;
  providers: ProviderSummary[];
  clientStats: ClientStats[];
};

export type ReferralItem = {
  id: number;
  createdAt: string | null;
  status: string | null;
  clientNames: string[];
};

export type ProviderDetail = {
  provider: ProviderSummary;
  address: RegistryAddress | null;
  organizationName: string | null;
  referrals: ReferralItem[];
};

export const PAGE_SIZE = 20;

export type Paged<T> = { items: T[]; hasMore: boolean; total: number };

export type OrganizationUpdate = {
  name: string;
  stateCode: string | null;
  partnerType: string | null;
  canReceiveUpdates: boolean;
  address: RegistryAddress | null;
};

export type ProviderUpdate = {
  firstName: string;
  lastName: string;
  email: string | null;
  phoneNumber: string | null;
  faxNumber: string | null;
  receiveUpdates: boolean;
  address: RegistryAddress | null;
};

function pagedPath(base: string, q: string, page: number): string {
  const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
  if (q !== "") {
    params.set("q", q);
  }
  return `${base}?${params.toString()}`;
}

export function listOrganizations(
  q: string,
  page: number,
): Promise<ApiResult<Paged<OrganizationSummary>>> {
  return api(pagedPath("/v1/referrals/external-organizations", q, page));
}

export function organizationDetail(id: string): Promise<ApiResult<OrganizationDetail>> {
  return api(`/v1/referrals/external-organizations/${encodeURIComponent(id)}`);
}

export function listProviders(
  q: string,
  page: number,
): Promise<ApiResult<Paged<ProviderSummary>>> {
  return api(pagedPath("/v1/referrals/external-providers", q, page));
}

export function providerDetail(id: string): Promise<ApiResult<ProviderDetail>> {
  return api(`/v1/referrals/external-providers/${encodeURIComponent(id)}`);
}

export function updateOrganization(
  id: number,
  body: OrganizationUpdate,
): Promise<ApiResult<OrganizationSummary>> {
  return api(`/v1/referrals/external-organizations/${id}`, mutatingPut(body));
}

export function updateProvider(
  id: number,
  body: ProviderUpdate,
): Promise<ApiResult<ProviderSummary>> {
  return api(`/v1/referrals/external-providers/${id}`, mutatingPut(body));
}
